/* eslint-disable no-param-reassign */
/*
 * @Descripttion:
 * @version:
 * @Author: lxh3@meitu.com
 * @Date: 2025-05-26 14:02:30
 * @LastEditors: zwh2 zwh2@meitu.com
 * @LastEditTime: 2025-06-27 15:06:38
 */
// web端专用token:
// beta: 315F4D4D9343434994287A78EAC04076

import { AxiosRequestConfig } from 'axios';
import { LanguageCode, useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import type { CountryCodeType } from '$/store/useOfficialStore/types';
import { generateUUID, isMobile } from '$/utils';
import APP from './api';

/** RoboNeo 请求扩展配置；个人作用域请求可按次跳过团队 Client-OID / Client-OGroupId。 */
export interface RoboneoRequestConfig<D = any> extends AxiosRequestConfig<D> {
  skipClientOId?: boolean;
  /**
   * 跳过全局中间件对 DEFAULT_ERROR / SHARE_CLOSE / history404 的 404 跳转，
   * 由调用方自行处理失败态。用于分享落地页等需要展示降级 UI 的场景。
   */
  skipErrorRedirect?: boolean;
}

export interface RoboneoCommonParams {
  token: string;
  gid: string;
  uid: string;
  trace_id: string;
  client_id: string;
  app_scene: 'neobiz' | 'roboneo';
  area_code: CountryCodeType;
  lang: LanguageCode;
  time_zone: string;
  [key: string]: any;
}

export interface RoboneoCanvasCommonParams {
  gnum: string;
  client_id: string;
  client_language: LanguageCode;
  country_code: CountryCodeType;
  time_zone: string;
  [key: string]: any;
}

/** 生成 Roboneo 普通接口公共参数，并同步写回已有请求配置。 */
export const addCommonParams = (config?: AxiosRequestConfig, extraParams: Record<string, any> = {}): RoboneoCommonParams => {
  const { uid } = useOfficialStore.getState().userInfo;
  const {
    gid, currentCountryCode, clientId, appScene,
  } = useOfficialStore.getState();
  const { languageCode } = useLanguageStore.getState().languageStore;
  const timeZone = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
  const commonParams: RoboneoCommonParams = {
    token: APP.roboneo.token(),
    gid,
    uid: String(uid ?? ''),
    trace_id: generateUUID(),
    client_id: clientId,
    app_scene: appScene || 'roboneo',
    area_code: currentCountryCode,
    lang: languageCode,
    time_zone: timeZone,
    ...(extraParams || {}),
  };
  if (config?.data?.parameter) {
    config.data.parameter = {
      ...commonParams,
      ...config.data.parameter,
    };
  }
  return commonParams;
};

/** 生成画布接口公共参数，并兼容 GET 与非 GET 请求的参数注入方式。 */
export const addCanvasCommonParams = (config?: AxiosRequestConfig, extraParams: Record<string, any> = {}): RoboneoCanvasCommonParams => {
  const {
    currentCountryCode, clientId, gid,
  } = useOfficialStore.getState();
  const { languageCode } = useLanguageStore.getState().languageStore;
  const timeZone = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
  const commonParams: RoboneoCanvasCommonParams = {
    gnum: gid,
    client_id: clientId,
    client_language: languageCode,
    country_code: currentCountryCode,
    time_zone: timeZone,
    ...(extraParams || {}),
  };
  if (config?.data) {
    config.data = {
      ...commonParams,
      ...config.data,
    };
  }
  if (config?.method === 'get') {
    config.params = {
      ...commonParams,
      ...config.params,
      ...(extraParams || {}),
    };
  }
  return commonParams;
};

/** 注入登录与客户端公共请求头，并允许个人作用域请求显式忽略当前团队 OID。 */
export async function addCommonHeaders(config?: RoboneoRequestConfig, extraHeaders: Record<string, any> = {}) {
  const { token } = useOfficialStore.getState().userInfo;
  const {
    clientId,
    clientOId,
    clientOGroupId,
  } = useOfficialStore.getState();

  // 在移动端，尝试从客户端获取最新的 token 与 uid
  // 先检查是否在客户端环境，避免 SSR 时执行
  let accessToken = token;
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    try {
      if (isMobile()) {
        const mobileAccountAuth = await getMobileAccountAuth();
        if (mobileAccountAuth?.encryptedToken) {
          accessToken = mobileAccountAuth.encryptedToken;
          // 更新全局 userInfo 中的 token 和 uid
          useOfficialStore.setState((state) => {
            state.userInfo.token = mobileAccountAuth.encryptedToken;
            if (mobileAccountAuth.uid) state.userInfo.uid = mobileAccountAuth.uid;
          });
        }
      }
    } catch (err) {
      console.warn('获取移动端鉴权信息失败:', err);
    }
  }
  const commonHeaders: Record<string, string> = {
    'access-token': accessToken,
    'client-id': clientId,
    ...(extraHeaders || {}),
  };
  if (!config?.skipClientOId) {
    if (clientOId != null) {
      commonHeaders['Client-OID'] = String(clientOId);
    }
    if (clientOGroupId) {
      commonHeaders['Client-OGroupId'] = clientOGroupId;
    }
  }
  if (config) {
    config.headers = {
      ...(config.headers || {}),
      ...commonHeaders,
    };
    delete config.skipClientOId;
  }
  return commonHeaders;
}

const SESSION_STORAGE_ACCESS_TOKEN_KEY = 'Access-Token';
const SESSION_STORAGE_ACCESS_TOKEN_EXPIRE_SECONDS_KEY = 'Access-Token-Expired-Seconds';
const SESSION_STORAGE_ACCESS_UID_KEY = 'Access-Token-Uid';

/** 移动端鉴权结果：加密 token + 用户 id，获取失败时为全空 */
export type MobileAccountAuthResult = { encryptedToken: string; uid: string; };

/**
 * 在移动端从客户端（Webview/Hogger）获取登录鉴权信息（加密 token + uid）
 * @returns 成功返回 { encryptedToken, uid }，失败返回 { encryptedToken: '', uid: '' }
 */
export async function getMobileAccountAuth(): Promise<MobileAccountAuthResult> {
  const emptyAuthResult: MobileAccountAuthResult = { encryptedToken: '', uid: '' };

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return emptyAuthResult;
  }
  if (!isMobile()) {
    return emptyAuthResult;
  }

  let getMeituAccountEncryptedToken: any;
  let HoggerCodeError: any;
  let HoggerTimeoutError: any;
  let getMeituAccountProfile: any;
  try {
    const hoggerModule = await import('@meitu/hogger');
    getMeituAccountEncryptedToken = hoggerModule.getMeituAccountEncryptedToken;
    getMeituAccountProfile = hoggerModule.getMeituAccountProfile;
    HoggerCodeError = hoggerModule.HoggerCodeError;
    HoggerTimeoutError = hoggerModule.HoggerTimeoutError;
  } catch (err) {
    console.warn('导入 @meitu/hogger 失败:', err);
    return emptyAuthResult;
  }

  const setMobileAuthCache = (encryptedToken: string, expireTimeSeconds: number, uid?: string) => {
    sessionStorage.setItem(SESSION_STORAGE_ACCESS_TOKEN_KEY, encryptedToken);
    sessionStorage.setItem(SESSION_STORAGE_ACCESS_TOKEN_EXPIRE_SECONDS_KEY, expireTimeSeconds.toString());
    if (uid !== undefined) sessionStorage.setItem(SESSION_STORAGE_ACCESS_UID_KEY, uid);
  };

  const clearMobileAuthCache = () => {
    sessionStorage.removeItem(SESSION_STORAGE_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_ACCESS_TOKEN_EXPIRE_SECONDS_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_ACCESS_UID_KEY);
  };

  const getMobileAuthCache = (): { cachedEncryptedToken: string | null; cachedUid: string; cachedExpireTimeSeconds: number; } => {
    return {
      cachedEncryptedToken: sessionStorage.getItem(SESSION_STORAGE_ACCESS_TOKEN_KEY),
      cachedUid: sessionStorage.getItem(SESSION_STORAGE_ACCESS_UID_KEY) ?? '',
      cachedExpireTimeSeconds: parseInt(sessionStorage.getItem(SESSION_STORAGE_ACCESS_TOKEN_EXPIRE_SECONDS_KEY) ?? '0', 10) || NaN,
    };
  };

  const { cachedEncryptedToken, cachedUid, cachedExpireTimeSeconds } = getMobileAuthCache();

  if (cachedEncryptedToken && cachedExpireTimeSeconds && cachedExpireTimeSeconds > Date.now() / 1000) {
    return { encryptedToken: cachedEncryptedToken, uid: cachedUid };
  }

  if (cachedEncryptedToken && cachedExpireTimeSeconds) {
    clearMobileAuthCache();
    return getMobileAccountAuth();
  }

  try {
    const [{ encryptedToken, expiredSeconds }] = await getMeituAccountEncryptedToken();
    const [{ id: profileUid }] = await getMeituAccountProfile();
    const uid = String(profileUid ?? '');

    if (!encryptedToken || expiredSeconds <= Date.now() / 1000) {
      clearMobileAuthCache();
      return emptyAuthResult;
    }

    setMobileAuthCache(encryptedToken, expiredSeconds, uid);
    return { encryptedToken, uid };
  } catch (err) {
    if (HoggerCodeError && err instanceof HoggerCodeError) {
      clearMobileAuthCache();
      return emptyAuthResult;
    }

    if (HoggerTimeoutError && err instanceof HoggerTimeoutError) {
      console.warn('HoggerTimeoutError', err);
      return await getMobileAccountAuth();
    }

    if (!(window as any).WebviewJsBridge) {
      return emptyAuthResult;
    }

    const pendingJsBridgeReady = new Promise<void>((resolve) => {
      window.addEventListener('WebviewJsBridgeReady', () => resolve(), false);
    });
    await pendingJsBridgeReady;
    return await getMobileAccountAuth();
  }
}
